import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "./Header.css";
import logo from "../../../assets/images/logo.png";
import UserDropdown from "./components/UserDropdown";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, logoutUser } from "../../../Store/userSlice";
import {
  FaBars, FaTimes, FaHome, FaCalendarAlt, FaTrophy,
  FaEnvelope, FaUser, FaSignInAlt, FaUserPlus,
  FaFacebook, FaInstagram, FaCalendarCheck, FaSignOutAlt,
} from "react-icons/fa";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const initials = user
    ? `${user.firstname?.[0] || ""}${user.lastname?.[0] || ""}`.toUpperCase()
    : "";

  // Enable sidebar transition only after first paint (prevents slide-in animation on load)
  useEffect(() => { setMounted(true); }, []);

  // Close sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // Lock body scroll when sidebar is open
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  // White header on all pages except home-at-top.
  // Runs immediately on every pathname change so the first render is correct.
  useEffect(() => {
    if (pathname !== "/") {
      setScrolled(true);
      return;
    }
    // Home page: follow scroll position
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll(); // read current position right away
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  const headerSolid = scrolled ? "header-solid" : "";

  const close = () => setSidebarOpen(false);

  const logout = () => {
    dispatch(logoutUser());
    close();
    window.location.assign("/login");
  };

  return (
    <>
      <header className={`site-header mo-left header header-transparent navstyle2 ${headerSolid}`}>
        <div className="sticky-header main-bar-wraper navbar-expand-lg">
          <div className="main-bar clearfix">
            <div className="container clearfix">

                {/* Logo */}
                <div className="site-logo">
                  <NavLink to="/">
                    <img src={logo} alt="Casa del Padel" />
                  </NavLink>
                </div>

                {/* Right pill */}
                <div className="content-nav">

                  {/* Hamburger — mobile only */}
                  <button
                    className={`nav-hamburger ${sidebarOpen ? "nav-hamburger--open" : ""}`}
                    onClick={() => setSidebarOpen((v) => !v)}
                    aria-label="Menu"
                  >
                    {sidebarOpen ? <FaTimes /> : <FaBars />}
                  </button>

                  {/* User section — always visible when logged in */}
                  {user && <UserDropdown />}

                  {/* Desktop horizontal nav */}
                  <nav className="desktop-nav">
                    <NavLink to="/" className={({ isActive }) => isActive ? "desktop-nav-link active" : "desktop-nav-link"}>Accueil</NavLink>
                    <NavLink to="/reservation-v2" className={({ isActive }) => isActive ? "desktop-nav-link active" : "desktop-nav-link"}>Réservation</NavLink>
                    <NavLink to="/tournois" className={({ isActive }) => isActive ? "desktop-nav-link active" : "desktop-nav-link"}>Tournois</NavLink>
                    <NavLink to="/contact" className={({ isActive }) => isActive ? "desktop-nav-link active" : "desktop-nav-link"}>Contact</NavLink>
                    {!user && (
                      <>
                        <NavLink to="/login" className={({ isActive }) => isActive ? "desktop-nav-link active" : "desktop-nav-link"}>Connexion</NavLink>
                        <NavLink to="/register" className={({ isActive }) => isActive ? "desktop-nav-link active" : "desktop-nav-link"}>S'inscrire</NavLink>
                      </>
                    )}
                  </nav>

                </div>
              </div>
            </div>
          </div>
      </header>

      {/* ── Backdrop ── */}
      <div
        className={`sidebar-backdrop ${sidebarOpen ? "sidebar-backdrop--visible" : ""}`}
        onClick={close}
      />

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${mounted ? "sidebar--ready" : ""} ${sidebarOpen ? "sidebar--open" : ""}`}>

        {/* Sidebar header */}
        <div className="sidebar-top">
          <img src={logo} alt="Casa del Padel" className="sidebar-logo" />
          <button className="sidebar-close-btn" onClick={close} aria-label="Fermer">
            <FaTimes />
          </button>
        </div>

        {/* User greeting */}
        {user && (
          <div className="sidebar-user">
            <div className="sidebar-user-initials">{initials}</div>
            <div className="sidebar-user-info">
              <span className="sidebar-user-name">{user.firstname} {user.lastname}</span>
              <span className="sidebar-user-email">{user.email}</span>
            </div>
          </div>
        )}

        {/* Nav links */}
        <nav className="sidebar-nav">
          <NavLink to="/" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
            <FaHome className="sidebar-link-icon" /> Accueil
          </NavLink>
          <NavLink to="/reservation-v2" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
            <FaCalendarCheck className="sidebar-link-icon" /> Réservation
          </NavLink>
          <NavLink to="/tournois" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
            <FaTrophy className="sidebar-link-icon" /> Tournois
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
            <FaEnvelope className="sidebar-link-icon" /> Contact
          </NavLink>

          {user && (
            <>
              <div className="sidebar-divider" />
              <NavLink to="/my-reservation" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
                <FaCalendarAlt className="sidebar-link-icon" /> Mes réservations
              </NavLink>
              <NavLink to="/profile" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
                <FaUser className="sidebar-link-icon" /> Mon profil
              </NavLink>
            </>
          )}

          {!user && (
            <>
              <div className="sidebar-divider" />
              <NavLink to="/login" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
                <FaSignInAlt className="sidebar-link-icon" /> Connexion
              </NavLink>
              <NavLink to="/register" className={({ isActive }) => `sidebar-link ${isActive ? "sidebar-link--active" : ""}`}>
                <FaUserPlus className="sidebar-link-icon" /> S'inscrire
              </NavLink>
            </>
          )}
        </nav>

        {/* Sidebar footer */}
        <div className="sidebar-footer">
          <div className="sidebar-socials">
            <a href="https://www.facebook.com/profile.php?id=61559890747854" target="_blank" rel="noreferrer" className="sidebar-social-btn" aria-label="Facebook">
              <FaFacebook />
            </a>
            <a href="https://www.instagram.com/casa_del_padel" target="_blank" rel="noreferrer" className="sidebar-social-btn" aria-label="Instagram">
              <FaInstagram />
            </a>
          </div>
          {user && (
            <button className="sidebar-logout" onClick={logout}>
              <FaSignOutAlt /> Se déconnecter
            </button>
          )}
        </div>

      </aside>
    </>
  );
};

export default Header;
